import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import type { InputNode, ReportListItem } from '../types';
import {
  ReportDropdown,
  reportMatchKey,
  type ReportListFetcher,
} from './reportDropdownShared';
import type { ResolveNameOutcome } from '../hooks/useReportAnalyze';
import { InfoIconTooltip } from './InfoIconTooltip';
import { RequiredMark } from './RequiredMark';

type Props = {
  node: InputNode;
  value: string[] | undefined;
  onChange: (key: string, value: string[]) => void;
  disabled?: boolean;
  error?: string;
  reportListFetch?: ReportListFetcher;
  onReportGoGenerate?: (agentType: string | number) => void;
  onViewReport?: (item: ReportListItem) => void;
  hideCurrentSessionReport?: boolean;
  /** 已解析的报告名，按 `${key}_${idx}` 索引；命中则该 slot 切到 name chip 模式 */
  reportNames?: Record<string, string>;
  /** slot 失焦时若为 URL，回调解析报告名（itemKey, url, nodeType）→ outcome */
  onResolveName?: (
    itemKey: string,
    url: string,
    nodeType: 'multi-variable' | 'multi-variable-select',
  ) => Promise<ResolveNameOutcome>;
  /** name chip 清除回调（itemKey） */
  onClearReport?: (itemKey: string) => void;
  /** 清空槽位（splice 前移/尾部截断）时同步重排 reportNames（nodeKey, removedIdx） */
  onShiftReportNames?: (nodeKey: string, removedIdx: number) => void;
};

// 基础 input 样式;ChipRow 内动态加 width。
// 必须 inline-block(不是 inline)— text-overflow:ellipsis 仅对 block container
// 生效,inline 元素会忽略;box-sizing:border-box 让 width 含 padding 一致;
// verticalAlign:middle 让 input 在 chip line-box 内垂直居中(与 ∨ 一致)。
// 注意：不在此设 minWidth——inline min-width 会盖过 CSS 的 min-width:0，导致 input 作为
// flex 子项无法收缩到容器内、长 URL 穿框。空槽最小宽改由 CSS (.rte-multi-variable input)
// 的 min-width 配 flex-basis 控制。
const INPUT_STYLE_BASE: React.CSSProperties = {
  display: 'inline-block',
  border: 0,
  background: 'transparent',
  outline: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  boxSizing: 'border-box',
  verticalAlign: 'middle',
};

const normalizeTooltip = (t: InputNode['tooltip']): string => {
  if (!t) return '';
  return typeof t === 'string' ? t : JSON.stringify(t);
};

/**
 * V1 parity (RichTextEditor/nodes/multi-variable/render.ts):
 * - chip 永远是 variable-chip + input
 * - 自动追加：rows = min(nonEmpty + 1, max)（用户填一个出来下一个空 chip）
 * - trailing icon：
 *   - reportListFetch 存在 → 紫色 ▼（共享 ReportDropdown）
 *   - 仅 tooltip 存在 → 紫色 ⓘ info-icon
 *   - 都没有 → 无
 * - 无 ⊖/➕ 按钮（V1 没有）
 */
export const MultiVariableNode: React.FC<Props> = React.memo(
  ({
    node,
    value,
    onChange,
    disabled,
    error,
    reportListFetch,
    onReportGoGenerate,
    onViewReport,
    hideCurrentSessionReport,
    reportNames,
    onResolveName,
    onClearReport,
    onShiftReportNames,
  }) => {
    const max = node.maxCount ?? 9;
    const min = node.minCount ?? 1;
    const tooltip = normalizeTooltip(node.tooltip);
    // V1 严格行为（htmlGenerator.ts:168 强制 hasReportDropdown=false）：
    // multi-variable 永远不挂 dropdown icon，无论 reportListFetch 是否传入。
    // 它就是普通的多变量文本输入（如商品 ID 列表）；要"选报告"用 multi-variable-select。
    // 仅 tooltip 存在时显示紫色 ⓘ info-icon。
    const showDropdown = false;
    const showInfoIcon = !!tooltip;

    const arr = useMemo(() => value ?? [], [value]);

    const rows = useMemo(() => {
      const nonEmpty = arr.filter((v) => v && v.trim().length > 0);
      // V1: 至少 min 个；非空 +1 个空槽（≤ max）
      const target = Math.max(min, Math.min(nonEmpty.length + 1, max));
      const len = Math.min(Math.max(arr.length, target), max);
      return Array.from({ length: len }, (_, i) => arr[i] ?? '');
    }, [arr, min, max]);

    const update = useCallback(
      (idx: number, v: string) => {
        const next = [...rows];
        next[idx] = v;
        // 清空某槽时（v 为空）：① 若是「中间空洞」（该槽之后仍有非空值）则前移补位删空洞；
        // ② 无论哪种清空，都裁掉数组**末尾的所有空串**——空槽只应由 rows 计算按需补 1 个，
        // 不能把 rows 的补位空串回写进 value，否则 arr.length 被撑大、残留删不掉的空框。
        if (v.trim() === '') {
          const lastNonEmpty = next.reduce(
            (acc, cur, i) => (cur && cur.trim() ? i : acc),
            -1,
          );
          if (idx < lastNonEmpty) {
            next.splice(idx, 1);
          }
          next.length = lastNonEmpty + 1; // 截到最后一个非空值（全空则归零）
          // 数组按 idx 前移/截断后，reportNames 的 `${key}_${idx}` 键须同步重排，
          // 否则报告名串到错误 slot / 残留删不掉（用户：混合输入删链接出错）。
          onShiftReportNames?.(node.key!, idx);
        }
        onChange(node.key!, next);
      },
      [rows, node.key, onChange, onShiftReportNames],
    );

    // 批量更新：从 startIdx 起填充 values（用于粘贴切分）
    const batchUpdate = useCallback(
      (startIdx: number, values: string[]) => {
        const next = [...rows];
        values.forEach((v, offset) => {
          const targetIdx = startIdx + offset;
          if (targetIdx < max) next[targetIdx] = v;
        });
        // 裁掉末尾空串
        const lastNonEmpty = next.reduce(
          (acc, cur, i) => (cur && cur.trim() ? i : acc),
          -1,
        );
        next.length = lastNonEmpty + 1;
        onChange(node.key!, next);
      },
      [rows, max, node.key, onChange],
    );

    const handleSelectReport = useCallback(
      (idx: number, item: ReportListItem) => {
        const next = [...rows];
        next[idx] = item.url;
        onChange(node.key!, next);
      },
      [rows, node.key, onChange],
    );

    // name chip 清除：清空该 slot 值。update(idx,'') 内部已通过 onShiftReportNames
    // 完成「丢弃被删槽名字 + 后续名字前移重排」，故**不能**再调 onClearReport——
    // 那会按旧 idx 二次删除，把刚前移过来的邻居名字一并删掉（用户：删一个后面那个变回链接文本框）。
    const handleClearSlot = useCallback(
      (idx: number) => {
        update(idx, '');
      },
      [update],
    );

    return (
      <RequiredMark node={node}>
      <span className={`rte-multi-variable ${error ? 'rte-multi-variable-error' : ''}`}>
        {rows.map((row, idx) => {
          const itemKey = `${node.key}_${idx}`;
          return (
            <ChipRow
              key={idx}
              nodeKey={node.key!}
              idx={idx}
              row={row}
              placeholder={node.placeholder}
              disabled={disabled}
              tooltip={tooltip}
              onUpdate={(v) => update(idx, v)}
              onBatchUpdate={(values) => batchUpdate(idx, values)}
              showDropdown={showDropdown}
              showInfoIcon={showInfoIcon}
              reportListFetch={reportListFetch}
              onReportGoGenerate={onReportGoGenerate}
              onViewReport={onViewReport}
              hideCurrentSessionReport={hideCurrentSessionReport}
              onSelectReport={(item) => handleSelectReport(idx, item)}
              nodeType="multi-variable"
              reportName={undefined}
              onResolveName={undefined}
              onClearName={undefined}
            />
          );
        })}
      </span>
      </RequiredMark>
    );
  },
);
MultiVariableNode.displayName = 'MultiVariableNode';

export const ChipRow: React.FC<{
  nodeKey: string;
  idx: number;
  row: string;
  placeholder?: string;
  disabled?: boolean;
  tooltip: string;
  onUpdate: (v: string) => void;
  showDropdown: boolean;
  showInfoIcon: boolean;
  reportListFetch?: ReportListFetcher;
  onReportGoGenerate?: (agentType: string | number) => void;
  onViewReport?: (item: ReportListItem) => void;
  hideCurrentSessionReport?: boolean;
  onSelectReport: (item: ReportListItem) => void;
  nodeType: 'multi-variable' | 'multi-variable-select';
  /** 该 slot 已解析的报告名；有值则渲染 name chip（替代 input） */
  reportName?: string;
  /** input 失焦且内容为 URL 时回调（传入解析）→ outcome，用于失败清空 + 报错 */
  onResolveName?: (url: string) => Promise<ResolveNameOutcome>;
  /** name chip 清除回调 */
  onClearName?: () => void;
  /** 批量更新：粘贴切分后从当前 slot 起填充多个值 */
  onBatchUpdate?: (values: string[]) => void;
}> = ({
  nodeKey,
  idx,
  row,
  placeholder,
  disabled,
  tooltip,
  onUpdate,
  onBatchUpdate,
  showDropdown,
  showInfoIcon,
  reportListFetch,
  onReportGoGenerate,
  onViewReport,
  hideCurrentSessionReport,
  onSelectReport,
  nodeType,
  reportName,
  onResolveName,
  onClearName,
}) => {
  const chipRef = useRef<HTMLSpanElement>(null);
  const itemKey = `${nodeKey}_${idx}`;
  const hasName = !!reportName;
  // V1 parity（multi-variable/render.ts）：info-icon 只在空槽（placeholder 态）显示。
  // isPlaceholder = !savedValue → 一旦该 slot 有值就隐藏 ⓘ，避免遮挡已填内容。
  // dropdown host 不受此限（V1 dropdown 始终挂、仅 hidden class 切换），故只 gate info-icon。
  // name chip 模式（hasName）也要 trailing padding 放清除按钮（合并 origin/develop）。
  const isEmpty = !(row || '').trim();
  const effectiveShowInfoIcon = showInfoIcon && isEmpty;
  const hasTrailingIcon = showDropdown || effectiveShowInfoIcon || hasName;
  const preciseMatchKey = useMemo(() => {
    if (hasName) return '';
    const trimmed = row.trim();
    return /^https?:\/\//i.test(trimmed) ? reportMatchKey(trimmed) : '';
  }, [hasName, row]);
  const selectedReportKey = useMemo(() => {
    const trimmed = row.trim();
    return /^https?:\/\//i.test(trimmed) ? reportMatchKey(trimmed) : '';
  }, [row]);

  // 报告名解析（失焦 / 粘贴共用）：内容是 http(s) URL 时尝试解析报告名（V1 analyzeHandlers.ts:403-425）。
  // ① 去重（lastResolvedRef）：同一 URL 不重复解析——失焦但内容没改（点进点出、切窗口回来）
  //    不该再打一次接口。粘贴/改值后 row 变化才会重置去重标记。
  // ② 失败处理对齐 V1（line 422 `.catch(() => 静默失败)`）：multi-variable 是**普通多变量文本输入**
  //    （商品 ID / 链接列表），里面放淘宝/京东等非报告链接完全合法。解析不出报告名是常态，
  //    **绝不能清空用户输入或报错**——只在成功时切 name chip（由上层 reportNames 驱动），
  //    失败/skip/stale 全部静默，保留用户原值。
  //    （早期 V2 误加了 failed→onUpdate('')+message.error，导致粘淘宝链接被清空+弹错，已移除。）
  const lastResolvedRef = useRef<string>('');
  const runResolve = useCallback(
    async (raw: string) => {
      if (!onResolveName) return;
      const url = (raw || '').trim();
      if (!/^https?:\/\//i.test(url)) return;
      if (lastResolvedRef.current === url) return; // 去重：同串不重复解析
      lastResolvedRef.current = url;
      // 结果（success → 上层 reportNames 切 name chip）无需在此处理；失败/stale/skip 全静默，
      // 不清空、不报错（对齐 V1：multi-variable 普通文本输入容纳任意链接）。
      await onResolveName(url);
    },
    [onResolveName],
  );

  const handleBlur = useCallback(() => {
    void runResolve(row);
  }, [runResolve, row]);

  // 粘贴即解析（V1 parity，对齐 ReportUrlNode）：拿 text/plain、preventDefault、写值后立即解析，
  // 不必等失焦（用户诉求「一粘贴就解析」）。
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text/plain');
      const trimmed = text.trim();

      // URL 链接：走解析流程（仅 multi-variable-select 或有 onResolveName 时）
      if (onResolveName && /^https?:\/\//i.test(trimmed)) {
        e.preventDefault();
        onUpdate(trimmed);
        document.dispatchEvent(new CustomEvent('rte-report-dropdown-opening', { detail: null }));
        void runResolve(trimmed);
        return;
      }

      // 普通文本且包含换行/空格：按分隔符切分，批量填充多个 slot
      if (onBatchUpdate && /[\n\s]/.test(text)) {
        e.preventDefault();
        // 按换行或连续空白切分，过滤空串
        const parts = text.split(/[\n\s]+/).filter(s => s.trim().length > 0);
        if (parts.length > 1) {
          onBatchUpdate(parts);
        } else if (parts.length === 1) {
          onUpdate(parts[0]);
        }
        return;
      }

      // 其他情况：浏览器默认粘贴行为
    },
    [onResolveName, onUpdate, onBatchUpdate, runResolve],
  );

  // row 被清空时重置去重标记（含失败清空 / 用户手动清），允许重新粘贴同一 URL 重试
  useLayoutEffect(() => {
    if (!(row || '').trim()) lastResolvedRef.current = '';
  }, [row]);

  // 用 hidden mirror span 测 row/placeholder 实际渲染宽度,给 input 显式 width
  // 让 chip 按内容自然撑开;不依赖硬编码字符宽度估算。
  // ∨ 让位由 chip 的 .variable-chip.rte-variable-with-icon padding-right:32 负责,
  // input 不需要重复 padding(避免双重 reserve 让 placeholder 离 ∨ 距离过大)。
  const mirrorRef = useRef<HTMLSpanElement>(null);
  const measureFor = row || placeholder || '';
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const remeasure = useCallback(() => {
    if (mirrorRef.current) setMeasuredWidth(mirrorRef.current.offsetWidth);
  }, []);
  useLayoutEffect(() => {
    remeasure();
    // 字体异步加载（CJK / 自定义字体）时，首帧 mirror 测出的宽度偏小 → input 被压窄 →
    // placeholder 被 text-overflow:ellipsis 截成省略号。字体就绪后再测一次校正。
    // fonts.ready 已 resolve 时回调同步触发，幂等无副作用。
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.ready) fonts.ready.then(remeasure);
  }, [measureFor, remeasure]);

  // +2px 余量：mirror span 与 input 占位符渲染存在亚像素差，width 取精确值时
  // scrollWidth 可能比 clientWidth 大 1px → 触发 text-overflow:ellipsis 误截断。
  const inputWidth = measuredWidth > 0 ? Math.max(40, measuredWidth + 2) : 40;
  // 不加 show-placeholder：chip 内已经有 <input>，input 自带 placeholder 渲染一份；
  // 再加 show-placeholder 会触发 ::after content: attr(data-placeholder) 叠加双重占位。
  // 通过 CSS .variable-chip input::placeholder 让 input 占位也变紫色。
  const chipClasses = [
    'variable-chip',
    hasName ? 'rte-report-name-chip' : '',
    hasTrailingIcon ? 'rte-variable-with-icon' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span
      ref={chipRef}
      className={chipClasses}
      data-type={nodeType === 'multi-variable-select' ? 'multi-variable-select' : 'variable'}
      data-key={itemKey}
      data-multi-key={nodeKey}
      data-index={idx}
      data-report-url={hasName ? row || true : undefined}
      data-report-name={reportName || undefined}
      {...(tooltip && !hasName && isEmpty ? { 'data-tooltip': tooltip, 'data-variable-key': nodeKey } : {})}
    >
      {/* hidden mirror,absolute 不影响布局,font:inherit 与 input 同尺寸 →
          offsetWidth 等于 input row/placeholder 实际渲染宽 */}
      <span
        ref={mirrorRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          font: 'inherit',
          pointerEvents: 'none',
          top: 0,
          left: 0,
        }}
      >
        {measureFor}
      </span>
      {hasName ? (
        <>
          <span
            className="rte-report-name-chip-label"
            onClick={() => {
              if (!showDropdown) return;
              const host = chipRef.current?.querySelector('.rte-report-dropdown-host') as HTMLElement | null;
              host?.click();
            }}
          >
            {reportName}
          </span>
          {showDropdown && reportListFetch && (
            <ReportDropdown
              chipRef={chipRef}
              nodeKey={itemKey}
              nodeType={nodeType}
              fetch={reportListFetch}
              onSelect={onSelectReport}
              onGoGenerate={onReportGoGenerate}
              onViewReport={onViewReport}
              hideCurrentSessionReport={hideCurrentSessionReport}
              preciseMatchKey={preciseMatchKey}
              selectedReportKey={selectedReportKey}
              quiet
            />
          )}
          {onClearName && (
            <span
              className="rte-report-chip-clear-host"
              onMouseDown={(e) => {
                // mousedown + preventDefault：避免点清除时 input 抢焦点触发额外失焦解析
                e.preventDefault();
                e.stopPropagation();
                onClearName();
              }}
            >
              <span className="rte-report-chip-clear-icon" title="清除">
                <CloseCircleFilled />
              </span>
            </span>
          )}
        </>
      ) : (
        <input
          data-type={nodeType === 'multi-variable-select' ? 'multi-variable-select' : 'variable'}
          data-key={itemKey}
          value={row}
          onChange={(e) => onUpdate(e.target.value)}
          onBlur={handleBlur}
          onPaste={handlePaste}
          onClick={() => {
            if (!showDropdown || row.trim()) return;
            const host = chipRef.current?.querySelector('.rte-report-dropdown-host') as HTMLElement | null;
            host?.click();
          }}
          onScroll={(e) => {
            // 原生 <input> 即使失焦 + text-overflow:ellipsis，文本超长时浏览器仍允许横向
            // 拖滚（用户反馈：省略号状态还能左右滚）。CSS 拦不住，失焦态强制 scrollLeft=0
            // 锁住省略号视图；聚焦编辑态不拦（跟随光标滚动是合理的原生行为）。
            const el = e.currentTarget;
            if (document.activeElement !== el && el.scrollLeft !== 0) {
              el.scrollLeft = 0;
            }
          }}
          disabled={disabled}
          placeholder={placeholder}
          style={{
            ...INPUT_STYLE_BASE,
            width: inputWidth,
          }}
        />
      )}
      {!hasName && showDropdown && reportListFetch && (
        <ReportDropdown
          chipRef={chipRef}
          nodeKey={itemKey}
          nodeType={nodeType}
          fetch={reportListFetch}
          onSelect={onSelectReport}
          onGoGenerate={onReportGoGenerate}
          onViewReport={onViewReport}
          hideCurrentSessionReport={hideCurrentSessionReport}
          preciseMatchKey={preciseMatchKey}
          selectedReportKey={selectedReportKey}
        />
      )}
      {!hasName && effectiveShowInfoIcon && (
        <InfoIconTooltip
          tooltip={tooltip}
          className="variable-info-icon"
          dataVariableKey={nodeKey}
        />
      )}
    </span>
  );
};
